/**
 * useExcel — XLSX import/export 훅 (클라이언트 + 서버사이드 병용).
 *
 * 【역할】
 * - 그리드/리스트 화면의 "엑셀 다운로드/업로드" 버튼을 한 줄 훅 호출로 구현.
 * - 소규모(~5k rows) 는 클라이언트 XLSX.js, 대용량은 서버 RPC → xlsx 스트림.
 *
 * 【주요 기능】
 * - exportToExcel(rows, columns, filename): 클라이언트 생성 + file-saver 다운로드
 * - importFromExcel(file): 업로드 파일 → 객체 배열 파싱
 * - exportServerSide(serviceName, datasets, columns, filename): 서버 사이드 대용량 export
 *
 * 【사용 예시】
 * ```tsx
 * const { exportToExcel, importFromExcel } = useExcel()
 * exportToExcel(rows, [{ key: 'id', header: 'ID' }, { key: 'name', header: '이름', width: 20 }])
 * const parsed = await importFromExcel(file)
 * ```
 *
 * 【왜 이 패턴?】
 * - xlsx 라이브러리는 동기 CPU 바운드라 10k+ 행에서 메인 스레드 블로킹.
 * - 대용량은 백엔드 /api/excel/export 가 RPC 재실행 → openpyxl 스트리밍 생성.
 */
import { useCallback } from 'react'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import http from './api/axios'

/** Excel 컬럼 스펙 — key 는 row 의 프로퍼티명, header 는 표시 라벨. */
export interface ExcelColumn {
  /** 데이터 행에서 추출할 프로퍼티 키 (예: 'userName') */
  key: string
  /** 시트에 표시될 헤더 텍스트 (이미 번역된 라벨 문자열) */
  header: string
  /** 열 너비 (문자 수 기준, 기본 15) */
  width?: number
  /** 셀 표시 형식 (현재 클라이언트 사이드는 미사용 — 서버 export 에서 활용) */
  format?: string
}

/**
 * useExcel — import/export 메서드 집합.
 * @returns exportToExcel/importFromExcel/exportServerSide.
 */
export function useExcel() {
  /**
   * 클라이언트 사이드 XLSX 생성 + 브라우저 다운로드.
   * @param rows - 데이터 행 배열.
   * @param columns - 헤더/매핑 스펙.
   * @param filename - 기본 'export.xlsx'.
   */
  const exportToExcel = useCallback(
    (rows: Record<string, unknown>[], columns: ExcelColumn[], filename = 'export.xlsx') => {
      const header = columns.map((c) => c.header)
      const body = rows.map((r) => columns.map((c) => r[c.key] ?? ''))
      const ws = XLSX.utils.aoa_to_sheet([header, ...body])
      if (columns.some((c) => c.width)) {
        ws['!cols'] = columns.map((c) => ({ wch: c.width ?? 15 }))
      }
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename)
    },
    []
  )

  /**
   * 업로드된 XLSX 파일의 첫 시트를 객체 배열로 파싱.
   * @param file - input[type=file] 에서 받은 File 객체.
   * @returns 헤더 row 를 key 로 쓰는 객체 배열.
   */
  const importFromExcel = useCallback(
    async <T = Record<string, unknown>>(file: File): Promise<T[]> => {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array' })
      const first = wb.SheetNames[0]
      const ws = wb.Sheets[first]
      return XLSX.utils.sheet_to_json<T>(ws)
    },
    []
  )

  /**
   * 서버사이드 Excel export — 대용량 (10k+ rows) 에 권장.
   * 백엔드 /api/excel/export (app/api/excel.py) 로 RPC 재실행 후 xlsx 스트림 반환.
   */
  const exportServerSide = useCallback(
    async (
      serviceName: string,
      datasets: Record<string, unknown>,
      columns: ExcelColumn[],
      filename = 'export.xlsx',
      sheetName = 'Sheet1'
    ) => {
      const res = await http.post(
        '/api/excel/export',
        { serviceName, datasets, columns, filename, sheetName },
        { responseType: 'blob' }
      )
      saveAs(new Blob([res.data as ArrayBuffer]), filename)
    },
    []
  )

  return { exportToExcel, importFromExcel, exportServerSide }
}
